import { createContext, useContext, useState, useEffect } from 'react';
import api, { refreshAccessToken } from '../api/client';
import { setAccessToken } from '../api/tokenStore';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // Stays true until the boot-time session restore settles. Consumers must treat
  // it as "restoring", distinct from "not authenticated" (user === null &&
  // !loading), otherwise a reload flashes the login page — see PrivateRoute.
  const [loading, setLoading] = useState(true);

  const refreshUser = async () => {
    try {
      const me = await api.get('/auth/me/');
      setUser(me.data);
    } catch { setUser(null); }
  };

  useEffect(() => {
    // The access token is memory-only, so a reload starts with none: the httpOnly
    // refresh cookie is the only thing left that can revive the session. Exactly
    // one attempt, and no /auth/me/ call when it fails — a visitor who never
    // logged in costs a single request and cannot loop.
    let cancelled = false;
    refreshAccessToken()
      .then(() => api.get('/auth/me/'))
      .then(({ data }) => { if (!cancelled) setUser(data); })
      .catch(() => { if (!cancelled) setUser(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const login = async (login, password) => {
    // Only the access token comes back in the body; the refresh token is set
    // by the backend as an httpOnly cookie (unreachable from JS).
    const { data } = await api.post('/auth/login/', { login, password });
    setAccessToken(data.access);
    const me = await api.get('/auth/me/');
    setUser(me.data);
  };

  const logout = async () => {
    // The backend reads the refresh token from the cookie, blacklists it and
    // clears the cookie — no token to send from JS.
    try { await api.post('/auth/logout/'); } catch {}
    setAccessToken(null); // after the call: /auth/logout/ needs the Bearer header
    const themeMode = localStorage.getItem('theme-mode');
    localStorage.clear();
    if (themeMode) localStorage.setItem('theme-mode', themeMode);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, loading, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
